import React, { useState, useEffect } from 'react';
import { getDashboard } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { FiHome, FiMapPin, FiUsers, FiDollarSign } from 'react-icons/fi';

const COLORS = ['#1565C0', '#4CAF50', '#FF9800', '#f44336', '#9C27B0', '#009688'];
const MONTHS = ['', 'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

const formatMoney = (n: number) => new Intl.NumberFormat('fr-FR').format(n);

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<any>(null);

  useEffect(() => {
    getDashboard().then(r => setStats(r.data)).catch(console.error);
  }, []);

  if (!stats) return <div className="loading">Chargement...</div>;

  const chartData = (stats.versementsMois || []).map((v: any) => {
    const [y, m] = v.mois.split('-');
    return { mois: MONTHS[parseInt(m)] + ' ' + y, total: parseInt(v.total) };
  });

  const pieData = (stats.versementsType || []).map((v: any) => ({
    name: v.libelle || 'Autre',
    value: parseInt(v.total)
  }));

  return (
    <div>
      <h1 className="page-title">Tableau de bord</h1>
      <p className="page-subtitle">Bonjour <strong>{user?.prenomnom}</strong>, bienvenue sur la plateforme CHA/MFB</p>

      <div className="kpi-grid">
        <div className="kpi-card" style={{ borderLeft: '4px solid #4CAF50' }}>
          <div className="kpi-icon green"><FiHome /></div>
          <div className="kpi-info">
            <div className="kpi-value">{stats.nbProjets}</div>
            <div className="kpi-label">Projets</div>
          </div>
        </div>
        <div className="kpi-card" style={{ borderLeft: '4px solid #FF9800' }}>
          <div className="kpi-icon yellow"><FiMapPin /></div>
          <div className="kpi-info">
            <div className="kpi-value">{formatMoney(stats.nbParcelles)}</div>
            <div className="kpi-label">Parcelles</div>
          </div>
        </div>
        <div className="kpi-card" style={{ borderLeft: '4px solid #f44336' }}>
          <div className="kpi-icon red"><FiUsers /></div>
          <div className="kpi-info">
            <div className="kpi-value">{formatMoney(stats.nbMembres)}</div>
            <div className="kpi-label">Membres</div>
          </div>
        </div>
        <div className="kpi-card" style={{ borderLeft: '4px solid #1565C0' }}>
          <div className="kpi-icon blue"><FiDollarSign /></div>
          <div className="kpi-info">
            <div className="kpi-value">{formatMoney(stats.totalVersements)}</div>
            <div className="kpi-label">Versements (FCFA)</div>
          </div>
        </div>
      </div>

      <div className="charts-row">
        <div className="chart-card">
          <h3>Évolution des cotisations</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mois" fontSize={12} />
              <YAxis fontSize={12} tickFormatter={v => (v / 1000000).toFixed(0) + 'M'} />
              <Tooltip formatter={(v: number) => formatMoney(v) + ' FCFA'} />
              <Bar dataKey="total" fill="#1565C0" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="chart-card">
          <h3>Répartition par type</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name }) => name}>
                {pieData.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v: number) => formatMoney(v) + ' FCFA'} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Versements par Direction */}
      {(stats.versementsDirection || []).length > 0 && (
        <div className="card">
          <div className="card-header">Montants versés par Direction</div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr><th>Direction</th><th>Membres</th><th style={{textAlign:'right'}}>Total versé (FCFA)</th><th style={{width:200}}>Progression</th></tr>
                </thead>
                <tbody>
                  {(() => {
                    const maxTotal = Math.max(...(stats.versementsDirection || []).map((d: any) => parseInt(d.total) || 0), 1);
                    return (stats.versementsDirection || []).map((d: any, i: number) => {
                      const total = parseInt(d.total) || 0;
                      const pct = Math.round((total / maxTotal) * 100);
                      return (
                        <tr key={i}>
                          <td><strong>{d.direction}</strong></td>
                          <td>{formatMoney(d.nb_membres)}</td>
                          <td style={{textAlign:'right', fontWeight: 600}}>{formatMoney(total)}</td>
                          <td>
                            <div style={{background:'#e3f2fd', borderRadius:6, height:20, overflow:'hidden', position:'relative'}}>
                              <div style={{background:'#1565C0', height:'100%', width: pct + '%', borderRadius:6, transition:'width 0.5s'}} />
                            </div>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
                <tfoot>
                  <tr className="total-row">
                    <td><strong>Total</strong></td>
                    <td><strong>{formatMoney((stats.versementsDirection || []).reduce((s: number, d: any) => s + parseInt(d.nb_membres || 0), 0))}</strong></td>
                    <td style={{textAlign:'right'}}><strong>{formatMoney((stats.versementsDirection || []).reduce((s: number, d: any) => s + (parseInt(d.total) || 0), 0))}</strong></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">Derniers membres inscrits</div>
        <div className="card-body">
          <table className="data-table">
            <thead>
              <tr><th>Nom</th><th>Email</th><th>Téléphone</th><th>Inscription</th></tr>
            </thead>
            <tbody>
              {(stats.recentMembers || []).map((m: any, i: number) => (
                <tr key={i}>
                  <td>{m.prenomnom}</td>
                  <td>{m.email || '-'}</td>
                  <td>{m.telephone || '-'}</td>
                  <td>{m.created ? new Date(m.created).toLocaleDateString('fr-FR') : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
